import cn from 'clsx';
import { Group, Stack } from '@mantine/core';
import { useState } from 'react';
import { Quote, QuoteRoute } from 'src/types';
import { Token } from 'src/components/Token/Token';
import { rangeFromZero } from 'src/utils';

function TokenBadge({ address }: { address: string }) {
  return (
    <Group
      gap={6}
      className={cn(
        'bg-[#141414] rounded-[44px] border-[1px] border-solid border-[rgba(243,243,243,0.12)] p-[6px]'
      )}
    >
      <Token.Icon address={address} size={22} />
      <Token.Symbol
        address={address}
        textProps={{ fz: 12, fw: 600, c: '#F3F3F3' }}
      />
    </Group>
  );
}

export function SwapRoutePlan({ quote }: { quote?: Quote }) {
  if (!quote) {
    return null;
  }

  const { inputMint, outputMint, routePlan } = quote;

  return (
    <Stack
      gap={0}
      align="center"
      className={cn(
        'w-full p-4',
        'bg-[rgba(25,25,25,0.24)] shadow-[0_5px_1.5px_-4px_rgba(8,8,8,0.09),0_6px_4px_-4px_rgba(8,8,8,0.05)]',
        'rounded-[16px] border-[rgba(243,243,243,0.08)] border-[1px] border-dashed'
      )}
    >
      <TokenBadge address={inputMint} />

      <Group gap={4} wrap="nowrap" className="w-full" grow>
        {routePlan.map((route) => {
          const parts = route.path.reduce(
            (acc, path) => {
              acc.push({
                type: 'line',
                addressOrName: path.label,
                key: path.ammKey,
              });

              if (path.outputMint !== outputMint) {
                acc.push({
                  type: 'tokenBadge',
                  addressOrName: path.outputMint,
                  key: path.ammKey,
                });
              }

              return acc;
            },
            [] as {
              type: 'line' | 'tokenBadge';
              addressOrName: string;
              key: string;
            }[]
          );

          return (
            <Stack
              key={route.path[0].ammKey}
              gap={0}
              align="center"
              className="shrink-0"
            >
              {parts.map((part) => {
                if (part.type === 'line') {
                  return (
                    <div
                      key={part.key}
                      className="relative w-[2px] h-[40px] bg-[rgba(243,243,243,0.08)]"
                    >
                      <div
                        className={cn(
                          'absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]',
                          'bg-[#141414]',
                          'text-[12px] font-[600] text-[#F3F3F3]'
                        )}
                      >
                        {part.addressOrName}
                      </div>
                    </div>
                  );
                }

                return (
                  <TokenBadge
                    key={part.addressOrName}
                    address={part.addressOrName}
                  />
                );
              })}
            </Stack>
          );
        })}
      </Group>

      <TokenBadge address={outputMint} />
    </Stack>
  );
}
